import { APP_CONFIG, ADVANCED_CONFIG } from '../config';
import { buildRuntimeRequest, selectRuntimeEndpoint } from './runtime';
import { createFunnelTracker } from './funnel';
import { createRuntimeApiError } from './runtimeError';
import { getRuntimeUserId } from './runtimeUser';
import { createPaymentIntent } from './paymentIntent';

const funnelTracker = createFunnelTracker({
  apiBaseUrl: APP_CONFIG.API_BASE_URL,
  appSlug: APP_CONFIG.APP_SLUG,
  apiKey: APP_CONFIG.API_KEY,
  bearerToken: APP_CONFIG.BEARER_TOKEN,
  appType: 'structured_report',
});

// API 客户端类
export class PromptaaSAPI {
  constructor() {
    this.baseURL = APP_CONFIG.API_BASE_URL;
    this.appSlug = APP_CONFIG.APP_SLUG;
    this.apiKey = APP_CONFIG.API_KEY;
  }

  // 获取请求头
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    // 优先使用Bearer Token，然后是API Key
    if (APP_CONFIG.BEARER_TOKEN) {
      headers['Authorization'] = APP_CONFIG.BEARER_TOKEN;
    } else if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }

    return headers;
  }

  getCreditEndpoint(name) {
    return APP_CONFIG.CREDIT_ENDPOINTS?.[name] || `/app/${this.appSlug}/credits/${name}/`;
  }

  getRuntimeUser(options = {}) {
    return getRuntimeUserId({
      appSlug: this.appSlug,
      explicitUserId: options.user || options.userId,
    });
  }

  // 创建超时信号
  createTimeoutSignal(timeout = ADVANCED_CONFIG.REQUEST_TIMEOUT) {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), timeout);
    return controller.signal;
  }

  getRuntimeEndpoint() {
    return selectRuntimeEndpoint({
      appType: APP_CONFIG.APP_TYPE,
      interactionMode: APP_CONFIG.INTERACTION_MODE,
      endpoints: APP_CONFIG.RUNTIME_ENDPOINTS,
    });
  }

  // 发送 completion 请求
  async sendCompletion(prompt, options = {}) {
    const url = `${this.baseURL}${this.getRuntimeEndpoint()}`;
    const requestBody = buildRuntimeRequest(prompt, {
      ...options,
      user: getRuntimeUserId({
        appSlug: this.appSlug,
        explicitUserId: options.user,
      }),
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody),
        signal: this.createTimeoutSignal()
      });

      if (!response.ok) {
        throw await createRuntimeApiError(response);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    }
  }

  async trackFunnelEvent(eventName, options = {}) {
    await funnelTracker.track(eventName, options);
  }

  async createPaymentIntent(options = {}) {
    return createPaymentIntent({
      apiBaseUrl: this.baseURL,
      appSlug: this.appSlug,
      bearerToken: APP_CONFIG.BEARER_TOKEN,
      apiKey: this.apiKey,
      amount: options.amount,
      currency: options.currency || 'usd',
    });
  }

  async getCreditPackages(options = {}) {
    const params = new URLSearchParams();
    if (options.currency) {
      params.set('currency', options.currency);
    }
    const query = params.toString();
    const response = await fetch(
      `${this.baseURL}${this.getCreditEndpoint('packages')}${query ? `?${query}` : ''}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
        signal: this.createTimeoutSignal(5000),
      },
    );
    if (!response.ok) {
      throw await createRuntimeApiError(response);
    }
    const data = await response.json();
    await this.trackFunnelEvent('credit.packages.viewed', {
      metadata: {
        currency: options.currency || '',
        package_count: Array.isArray(data.packages) ? data.packages.length : 0,
      },
    });
    return data;
  }

  async getCreditBalance(options = {}) {
    const user = this.getRuntimeUser(options);
    const response = await fetch(
      `${this.baseURL}${this.getCreditEndpoint('balance')}?user=${encodeURIComponent(user)}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
        signal: this.createTimeoutSignal(5000),
      },
    );
    if (!response.ok) {
      throw await createRuntimeApiError(response);
    }
    const data = await response.json();
    await this.trackFunnelEvent('credit.balance.viewed', {
      userId: user,
      metadata: { balance: Number(data.balance || 0) },
    });
    return data;
  }

  async getCreditTransactions(options = {}) {
    const user = this.getRuntimeUser(options);
    const response = await fetch(
      `${this.baseURL}${this.getCreditEndpoint('transactions')}?user=${encodeURIComponent(user)}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
        signal: this.createTimeoutSignal(5000),
      },
    );
    if (!response.ok) {
      throw await createRuntimeApiError(response);
    }
    return await response.json();
  }

  async createCreditCheckoutSession({ packageId, user, userId } = {}) {
    const runtimeUserId = this.getRuntimeUser({ user, userId });
    const response = await fetch(`${this.baseURL}${this.getCreditEndpoint('checkout')}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        package_id: packageId,
        user: runtimeUserId,
      }),
      signal: this.createTimeoutSignal(10000),
    });
    if (!response.ok) {
      throw await createRuntimeApiError(response);
    }
    const data = await response.json();
    await this.trackFunnelEvent('credit.checkout.started', {
      userId: runtimeUserId,
      metadata: {
        package_id: data.package?.id || packageId || '',
        credits: Number(data.package?.credits || 0),
        amount: Number(data.package?.price || 0),
        currency: data.package?.currency || '',
      },
    });
    return data;
  }

  async verifyCreditPayment({ sessionId } = {}) {
    const response = await fetch(`${this.baseURL}${this.getCreditEndpoint('verify')}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ session_id: sessionId }),
      signal: this.createTimeoutSignal(10000),
    });
    if (!response.ok) {
      throw await createRuntimeApiError(response);
    }
    const data = await response.json();
    if (data.status === 'success') {
      await this.trackFunnelEvent('credit.purchase.completed', {
        metadata: {
          credits: Number(data.payment?.credits || 0),
          amount: Number(data.payment?.amount || 0),
          status: data.payment?.status || data.status,
        },
      });
    }
    return data;
  }

  async warmRuntimeConfig() {
    if (!this.baseURL || !this.appSlug || this.appSlug === 'dream-lens-dogfood') {
      return null;
    }

    try {
      const response = await fetch(`${this.baseURL}/app/${this.appSlug}/runtime-config`, {
        method: 'GET',
        signal: this.createTimeoutSignal(5000),
      });
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (_error) {
      return null;
    }
  }

  // 验证配置
  validateConfig() {
    if (!this.appSlug || this.appSlug === 'dream-lens-dogfood') {
      throw new Error('Please configure your APP_SLUG in src/config.js');
    }
    if (!this.baseURL) {
      throw new Error('Please configure your API_BASE_URL in src/config.js');
    }
  }
}

// 创建全局 API 实例
export const api = new PromptaaSAPI();
